import { useRouter } from "next/router";
import { useRef, useState, useMemo } from "react";
import { Button, Dropdown, Form, Modal } from "react-bootstrap";
import cookie from "js-cookie";
import { countries } from "../../constants";
import ReactCountryFlag from "react-country-flag";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

/**
 * Function to upload the audio to the server.
 * @param {string} LeadID - The Lead ID to associate the audio with.
 * @param {Blob} audioBlob - The audio blob to be uploaded.
 * @param {string} recordedText - The transcribed text from the audio.
 */
export const uploadAudio = async (LeadID, audioBlob, recordedText) => {
  if(!LeadID || !audioBlob || !recordedText) {
    console.error("Missing required parameters for audio upload");
    return;
  }
  // Create a FormData object to hold the data to be sent


  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.wav");
  formData.append("LeadID", LeadID);
  formData.append("Audio_Text", recordedText);

  try {
    // Make a POST request to the server with the FormData
    await axios.post("https://obl-js.orientbell.com/uploadAudio", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (err) {
    // Log any errors that occur during the upload
    console.error("Upload error", err);
  }
};

/**
 * A Modal component for getting in touch with our tile's expert.
 * @param {Object} props
 * @param {boolean} props.TimePopup - Whether to show the 15 second popup or not.
 * @param {function} props.submitGetInTouch - Function to call when the form is submitted.
 * @param {function} props.onHide - Function to call when the modal is closed.
 */
export function GetInTouchModal({ show, TimePopup, submitGetInTouch, onHide }) {
  const router = useRouter();
  const isExportPage = useMemo(() => {
    return (
      router.asPath.includes("/tile-exports") ||
      router.asPath.includes("/download-export-catalogue")
    );
  }, [router.asPath]);

  const [recordedText, setRecordedText] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [recording, setRecording] = useState(false);

  /**
   * Reference to the media recorder object.
   */
  const mediaRecorderRef = useRef(null);
  /**
   * Reference to the audio chunks array.
   */
  const audioChunksRef = useRef([]);

  const formik = useFormik({
    initialValues: {
      name: "",
      number: "",
      email: "",
      pincode: cookie.get("pincode") || "",
      countryCode: "",
      cc: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Name is required")
        .matches(
          /^[a-zA-Z\s]+$/,
          "Name should only contain letters and spaces"
        ),
      number: isExportPage
        ? // for worldwide exports, number should be numeric and 6-16 digits
          Yup.string()
            .matches(/^[0-9]+$/, "Only numbers are allowed")
            .min(6, "Invalid number")
            .max(16, "Invalid number")
            .required("Number is required")
        : // for India, number should be alphanumeric and 10 digits
          Yup.string()
            .required("Number is required")
            .matches(/^[a-zA-Z0-9]+$/, "Mobile number must be alphanumeric")
            .matches(/^[0-9]+$/, "Only numbers are allowed")
            .length(10, "Invalid number"),
      email: Yup.string(),
      pincode: Yup.string().nullable().length(6, "Invalid pincode"),
    }),
    onSubmit: (values, { resetForm, setSubmitting, setErrors }) => {
      try {
        const label = TimePopup
          ? isExportPage
            ? "Get In Touch 15_sec_Pop-up export"
            : "Get In Touch 15_sec_Pop-up"
          : isExportPage
          ? "Get In Touch Tariq export"
          : "Get In Touch Tariq";

        submitGetInTouch(
          label,
          {
            ...values,
            audioBlob,
            recordedText,
          },
          "GetInTouch"
        );

           if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "mkt-form-view",
        action: "submitted",
        popup_id: "tareeq-figurine-view",
      });
    }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrors({ general: "Something went wrong. Please try again." });
      } finally {
        setSubmitting(false);
        resetForm();
        if (audioBlob) {
          uploadAudio(values.LeadID || "Get In Touch", audioBlob, recordedText);
        }
      }
    },
  });

  /**
   * Function to start recording the audio.
   */
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorderRef.current = new MediaRecorder(stream);
    mediaRecorderRef.current.ondataavailable = (e) => {
      audioChunksRef.current.push(e.data);
    };
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: "audio/wav" });
      setAudioBlob(blob);
      audioChunksRef.current = [];
    };
    mediaRecorderRef.current.start();
    setRecording(true);

    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onresult = (e) => {
      const currentTranscript = e.results[e.results.length - 1][0].transcript;
      setRecordedText(currentTranscript);
    };
    recognition.onerror = (e) => console.log("Speech error:", e.error);
    recognition.onend = () => console.log("Speech ended");

    recognition.start();
  };

  /**
   * Function to stop recording the audio.
   */
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <Modal
      show={show}
      className="pincode-popup-bx expert_assistance_modal ask-question-popup"
     onShow={() => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "mkt-form-view",
        action: "shown",
        popup_id: "tareeq-figurine-view",
      });
    }
  }}
  onHide={() => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "mkt-form-view",
        action: "closed",
        popup_id: "tareeq-figurine-view",
      });
    }
    // still call the parent handler to actually hide it
    onHide();
  }}
    >
      <Modal.Dialog>
        <Modal.Header closeButton >
          <Modal.Title>
            {TimePopup ? "Get in touch with our tile's expert" : "Get In Touch"}
          </Modal.Title>
        </Modal.Header>
        <div className="ask-question-section" id='tareeq-form-view'>
          <div className="expert-fig-bx">
            <img src="/new-tareek.jpg" />
          </div>
          <Modal.Body>
            <h2>Ask Tile Expert</h2>
            <p>Let Experts Help You In Buying Process</p>
            <Form onSubmit={formik.handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name*</Form.Label>
                <Form.Control
                  name="name"
                  type="text"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  isInvalid={formik.touched.name && !!formik.errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Mobile*</Form.Label>
                {isExportPage && (
                  <div className="export-page-getInTouchTariq">
                    <Dropdown
                      onSelect={(code) => {
                        const country = countries.find((c) => c.code === code);

                        formik.setFieldValue("countryCode", code);
                        formik.setFieldValue("cc", country.cc);
                      }}
                    >
                      <Dropdown.Toggle variant="secondary">
                        {formik.values.countryCode ? (
                          <>
                            <ReactCountryFlag
                              countryCode={formik.values.cc}
                              svg
                            />
                            {` (${formik.values.countryCode})`}
                          </>
                        ) : (
                          "Country Code"
                        )}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {countries.map((country, index) => (
                          <Dropdown.Item key={index} eventKey={country.code}>
                            <ReactCountryFlag countryCode={country.cc} svg />
                            {`${country.name} (${country.code})`}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                    <Form.Control
                      type="number"
                      name="number"
                      onChange={formik.handleChange}
                      value={formik.values.number}
                      isInvalid={
                        formik.touched.number && !!formik.errors.number
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.number}
                    </Form.Control.Feedback>
                  </div>
                )}
                {!isExportPage && (
                  <>
                    <Form.Control
                      type="number"
                      name="number"
                      onChange={formik.handleChange}
                      value={formik.values.number}
                      isInvalid={
                        formik.touched.number && !!formik.errors.number
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.number}
                    </Form.Control.Feedback>
                  </>
                )}
              </Form.Group>

              {isExportPage ? (
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    onChange={formik.handleChange}
                    value={formik.values.email}
                    isInvalid={formik.touched.email && !!formik.errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              ) : (
                <Form.Group className="mb-3">
                  <Form.Label>Pincode</Form.Label>
                  <Form.Control
                    type="number"
                    name="pincode"
                    onChange={formik.handleChange}
                    value={formik.values.pincode}
                    isInvalid={
                      formik.touched.pincode && !!formik.errors.pincode
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.pincode}
                  </Form.Control.Feedback>
                </Form.Group>
              )}

              <div className="recording">
                <button
                  type="button"
                  onClick={recording ? stopRecording : startRecording}
                >
                  {recording ? (
                    <>
                      <img src="/recording.gif" alt="mic" /> Stop Recording
                    </>
                  ) : (
                    <>
                      <img src="/nic-ing.png" alt="mic" /> Record your Audio
                    </>
                  )}
                </button>
              </div>

              {audioBlob && (
                <div>
                  <audio controls src={URL.createObjectURL(audioBlob)} />
                </div>
              )}

              <Button
                type="submit"
                disabled={formik.isSubmitting}
                style={{ width: "100%" }}
              >
                {formik.isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Form>
          </Modal.Body>
        </div>

       
      </Modal.Dialog>
    </Modal>
  );
}
